// src/pages/CheckoutPage.js
import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import toast from "react-hot-toast";
import { apiRequest } from "../services/api";
import { getActiveCouponsCount } from "../services/coupons.api";

function CheckoutPage() {
  const { lang } = useContext(LanguageContext);
  const { cartItems, subtotal, discount } = useCart();
  const navigate = useNavigate();

  /* ---------------- STATE ---------------- */
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState(null);
  const [hasActiveCoupons, setHasActiveCoupons] = useState(true);

  const [shippingMethod, setShippingMethod] = useState("delivery");

  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [governorate, setGovernorate] = useState("");
  const [address, setAddress] = useState("");

  const [note, setNote] = useState("");
  const [noteFocused, setNoteFocused] = useState(false);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [shipping, setShipping] = useState({
    fee: 4,
    free_enabled: false,
    free_threshold: 0,
  });

  /* ---------------- DATA ---------------- */
  useEffect(() => {
    apiRequest("/shipping-settings")
      .then((s) =>
        setShipping({
          fee: Number(s.default_fee ?? 4),
          free_enabled: !!s.free_enabled,
          free_threshold: Number(s.free_threshold ?? 0),
        })
      )
      .catch(() => {});
  }, []);

  useEffect(() => {
    getActiveCouponsCount()
      .then((res) => setHasActiveCoupons(res.count > 0))
      .catch(() => setHasActiveCoupons(false));
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem("esill-user");
    if (!stored) return;
    const u = JSON.parse(stored);
    setFullName(u.full_name || "");
    setPhone(u.phone || "");
    setEmail(u.email || "");
  }, []);

  /* ---------------- SHIPPING ---------------- */
  const isFreeDelivery =
    shipping.free_enabled && subtotal >= shipping.free_threshold;

  const deliveryFee =
    shippingMethod === "pickup" ? 0 : isFreeDelivery ? 0 : shipping.fee;

  /* ---------------- GOVERNORATES ---------------- */
  const LEB_GOV = [
    { en: "Beirut", ar: "بيروت" },
    { en: "Mount Lebanon", ar: "جبل لبنان" },
    { en: "North Lebanon", ar: "لبنان الشمالي" },
    { en: "South Lebanon", ar: "لبنان الجنوبي" },
    { en: "Bekaa", ar: "البقاع" },
    { en: "Baalbek-Hermel", ar: "بعلبك الهرمل" },
    { en: "Nabatieh", ar: "النبطية" },
    { en: "Akkar", ar: "عكار" },
  ];

  /* ---------------- TEXT ---------------- */
  const t = {
    EN: {
      title: "CHECKOUT",
      contact: "Contact Information",
      shippingMethod: "Shipping Method",
      delivery: "Delivery Address",
      payment: "Payment Method",
      summary: "Order Summary",
      name: "Full Name",
      phone: "Phone Number",
      email: "Email (optional)",
      city: "Governorate",
      address: "City / Street / Building / Floor",
      note: "Note (optional)",
      cod: "Cash on Delivery",
      placeOrder: "PLACE ORDER",
      required: "Please fill all required fields",
      couponSuccess: "Coupon applied successfully",
couponInvalid: "Invalid coupon code",
couponExpired: "This coupon has expired",
    },
    AR: {
      title: "إتمام الشراء",
      contact: "معلومات التواصل",
      shippingMethod: "طريقة الشحن",
      delivery: "عنوان التوصيل",
      payment: "طريقة الدفع",
      summary: "ملخص الطلب",
      name: "الاسم الكامل",
      phone: "رقم الهاتف",
      email: "البريد الإلكتروني (اختياري)",
      city: "المحافظة",
      address: "المدينة / الشارع / المبنى / الطابق",
      note: "ملاحظة (اختياري)",
      cod: "الدفع عند الاستلام",
      placeOrder: "إتمام الطلب",
      required: "يرجى ملء جميع الخانات المطلوبة",
      couponSuccess: "تم تطبيق الكوبون بنجاح",
couponInvalid: "رمز الخصم غير صالح",
couponExpired: "انتهت صلاحية هذا الكوبون",
    },
  }[lang];

  /* ---------------- HELPERS ---------------- */
  const formatLbLocal = (digits) => {
  const d = digits.slice(0, 8);
  const p1 = d.slice(0, 2);
  const p2 = d.slice(2, 5);
  const p3 = d.slice(5, 8);
  return [p1, p2, p3].filter(Boolean).join(" ");
};

const formatLbPlus961 = (digitsAfter961) => {
  const d = digitsAfter961.slice(0, 8);
  const p1 = d.slice(0, 2);
  const p2 = d.slice(2, 5);
  const p3 = d.slice(5, 8);
  const parts = [p1, p2, p3].filter(Boolean).join(" ");
  return parts ? `+961 ${parts}` : "+961";
};

const formatPhoneInput = (raw = "") => {
  const v = raw.trim();

  // +961 flow
  if (v.startsWith("+961")) {
    const after = v.replace(/\s/g, "").slice(4);
    const digits = after.replace(/\D/g, "");
    return formatLbPlus961(digits);
  }

  // Lebanese local (70 / 71 / 03 / etc.)
  const firstChar = v.replace(/\s/g, "").charAt(0);
  if (firstChar === "0" || firstChar === "7" || firstChar === "8") {
    const digits = v.replace(/\D/g, "").slice(0, 8);
    return formatLbLocal(digits);
  }

  // International (leave as-is)
  return raw;
};


  const validate = () => {
    let e = {};
    if (!fullName || fullName.length < 3) e.fullName = true;

    const clean = phone.replace(/\D/g, "");
    const reg = /^(961|0)?(3|70|71|76|78|79|81)\d{6}$/;
    if (!reg.test(clean)) e.phone = true;

    if (shippingMethod === "delivery") {
      if (!governorate) e.governorate = true;
      if (!address || address.length < 5) e.address = true;
    }

    setErrors(e);
    if (Object.keys(e).length) {
      toast.error(t.required);
      return false;
    }
    return true;
  };

  /* ---------------- COUPON ---------------- */
  const applyCoupon = async () => {
  if (!couponCode.trim()) return;
  setCouponLoading(true);

  try {
    const res = await apiRequest("/coupons/validate", {
      method: "POST",
      body: JSON.stringify({ code: couponCode, subtotal }),
    });

    setCoupon(res);
    setCouponError(null);
  } catch (err) {
    setCoupon(null);
    setCouponError(err?.message || "invalid");
  } finally {
    setCouponLoading(false);
  }
};



  /* ---------------- SUBMIT ---------------- */
  const submitOrder = async () => {
    if (!cartItems.length) {
      toast.error(lang === "EN" ? "Cart is empty" : "السلة فارغة");
      return;
    }
    if (!validate()) return;

    setLoading(true);
    let token = localStorage.getItem("esill-token");
    const sessionToken = localStorage.getItem("esill-session");

    try {
      // 🔥 GUEST CHECKOUT FLOW
if (!token) {
  // 1️⃣ Create guest user
  const guest = await apiRequest("/auth/guest", {
    method: "POST",
    body: JSON.stringify({
      full_name: fullName,
      phone,
      email: email || null,
    }),
  });

  // 🔔 Existing account warning (optional but recommended)
  if (guest.existed) {
    toast(
      lang === "EN"
        ? `You already have an account with this phone number:\n${guest.user.phone}`
        : `لديك حساب موجود مسبقاً بهذا الرقم:\n${guest.user.phone}`,
      { icon: "ℹ️", duration: 4000 }
    );
  }

  // 2️⃣ Save auth
  localStorage.setItem("esill-token", guest.token);
  localStorage.setItem("esill-user", JSON.stringify(guest.user));
  token = guest.token;

  // 3️⃣ Merge guest cart
  if (sessionToken) {
    await apiRequest("/cart/merge", {
      method: "POST",
      body: JSON.stringify({ session_token: sessionToken }),
    });
    localStorage.removeItem("esill-session");
  }
}

      const res = await apiRequest("/orders", {
  method: "POST",
  body: JSON.stringify({
    full_name: fullName,
    phone,
    email: email || null,
    city: shippingMethod === "delivery" ? governorate : null,
    street: shippingMethod === "delivery" ? address : null,
    notes: note || null,
    payment_method: "cod",
    coupon_code: coupon?.code || null,
    shipping_method: shippingMethod,
  }),
});

// ✅ STORE ORDER FOR THANK YOU PAGE
sessionStorage.setItem(
  "esill-order",
  JSON.stringify({
    order_id: res.order_id,
    order_number: res.order_number,
    subtotal: subtotal,
    discount: coupon?.discount || 0,
    delivery_fee: deliveryFee,
    total:
      subtotal -
      (coupon?.discount || 0) +
      deliveryFee,
    itemCount: cartItems.reduce((a, b) => a + b.qty, 0),
    shipping_method: shippingMethod,
    full_name: fullName,
    phone,
    email,
    city: governorate,
    street: address,

    // 🔥 ADD THIS
    items: cartItems.map((item) => ({
      product_id: item.product_id,
      public_id: item.public_id,
      qty: item.qty,
      price: item.price,
    })),
  })
);


toast.success(lang === "EN" ? "Order placed!" : "تم إرسال الطلب!");
navigate("/thank-you");

    } catch (err) {
      toast.error(err.message || "Order failed");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- UI ---------------- */
  const inputCls = (f) =>
    `w-full px-4 py-3 mb-2 rounded-md bg-[#0F1A12] text-white border ${
      errors[f] ? "border-red-500" : "border-[#2E5A2A]"
    }`;

  return (
    <div className="w-full min-h-screen px-5 pt-[95px] pb-24 bg-[#0F1A12] text-white">
      <button
        onClick={() => navigate(-1)}
        className="absolute top-[80px] left-4 text-3xl opacity-70"
      >
        ×
      </button>

      <h1 className="text-xl font-semibold text-center">{t.title}</h1>
      <div className="h-[2px] w-16 bg-[#2E5A2A] mx-auto mt-2 mb-6"></div>

      {/* CONTACT */}
      <section className="mb-8">
        <h2 className="font-bold mb-3">{t.contact}</h2>
        <input className={inputCls("fullName")} placeholder={t.name} value={fullName} onChange={(e) => setFullName(e.target.value)} />
        <input
  className={inputCls("phone")}
  placeholder={t.phone}
  value={phone}
  onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
/>
        <input className={inputCls("email")} placeholder={t.email} value={email} onChange={(e) => setEmail(e.target.value)} />
      </section>

      {/* SHIPPING METHOD */}
      <section className="mb-8">
        <h2 className="font-bold mb-3">{t.shippingMethod}</h2>
        <label className="flex gap-3 mb-2"><input type="radio" checked={shippingMethod==="delivery"} onChange={()=>setShippingMethod("delivery")} /> Delivery</label>
        <label className="flex gap-3"><input type="radio" checked={shippingMethod==="pickup"} onChange={()=>setShippingMethod("pickup")} /> Pickup</label>
      </section>

      {/* DELIVERY */}
      {shippingMethod === "delivery" && (
        <section className="mb-8">
          <h2 className="font-bold mb-3">{t.delivery}</h2>
          <select value={governorate} onChange={(e)=>setGovernorate(e.target.value)} className={inputCls("governorate")}>
            <option value="">{t.city}</option>
            {LEB_GOV.map((g)=>(
              <option key={g.en} value={g.en}>{lang==="EN"?g.en:g.ar}</option>
            ))}
          </select>
          <textarea className={inputCls("address")} placeholder={t.address} value={address} onChange={(e)=>setAddress(e.target.value)} />
        </section>
      )}

      {/* NOTE */}
      <section className="mb-8">
        <textarea className={inputCls()} placeholder={t.note} value={note} onChange={(e)=>setNote(e.target.value)} />
      </section>

      {/* PAYMENT */}
      <section className="mb-8">
        <h2 className="font-bold mb-3">{t.payment}</h2>
        <label className="flex gap-3"><input type="radio" checked readOnly /> {t.cod}</label>
      </section>

      {/* SUMMARY */}
      <section className="mb-10">
        <h2 className="font-bold mb-3">{t.summary}</h2>
        {/* ORDER ITEMS */}
<div className="mb-4 space-y-3">
  {cartItems.map((item) => (
    <div
      key={item.id}
      className="flex items-center gap-3 border-b border-white/10 pb-3"
    >
      <img
        src={item.image}
        alt=""
        className="w-14 h-14 rounded-md object-cover bg-black"
      />

      <div className="flex-1">
        <p className="text-sm font-medium leading-tight">
          {lang === "EN" ? item.nameEN : item.nameAR}
        </p>

        {/* COLOR + SIZE */}
        {(item.color || item.size) && (
          <div className="flex items-center gap-2 mt-1">
            {item.color && (
              <span
                className="w-3 h-3 rounded-full border border-white/40"
                style={{ backgroundColor: item.color }}
                title={item.color}
              />
            )}

            {item.size && (
              <span className="text-[11px] px-2 py-[2px] rounded-full border border-white/30">
                {lang === "EN" ? `Size ${item.size}` : `مقاس ${item.size}`}
              </span>
            )}
          </div>
        )}

        <p className="text-xs opacity-60 mt-1">
          {item.qty} × ${item.price}
        </p>
      </div>
    </div>
  ))}
</div>

        {hasActiveCoupons && (
          <div className="flex gap-2 mb-3">
            <input className="flex-1 px-3 py-2 rounded bg-[#0F1A12] border border-[#2E5A2A]" value={couponCode} onChange={(e)=>setCouponCode(e.target.value)} placeholder="Coupon code" />
            <button onClick={applyCoupon} className="px-4 bg-[#2E5A2A] rounded">{couponLoading?"...":"Apply"}</button>
          </div>
        )}
        {/* COUPON MESSAGE */}
{coupon && (
  <p className="text-green-500 text-sm mb-3">
    {t.couponSuccess}
  </p>
)}

{couponError === "expired" && (
  <p className="text-red-500 text-sm mb-3">
    {t.couponExpired}
  </p>
)}

{couponError === "unavailable" && (
  <p className="text-red-500 text-sm mb-3">
    {t.couponInvalid}
  </p>
)}


        <div className="flex justify-between"><span>Subtotal</span><span>${subtotal}</span></div>
        {coupon && <div className="flex justify-between text-green-500"><span>Discount</span><span>- ${coupon.discount}</span></div>}
        <div className="flex justify-between items-center">
  <span>
    {shippingMethod === "pickup"
      ? lang === "EN"
        ? "Pickup from store"
        : "استلام من المتجر"
      : lang === "EN"
      ? "Delivery"
      : "التوصيل"}
  </span>

  <span
    className={
      shippingMethod === "pickup"
        ? "font-semibold text-green-500"
        : isFreeDelivery
        ? "font-semibold text-red-500"
        : ""
    }
  >
    {shippingMethod === "pickup"
      ? lang === "EN"
        ? "Free"
        : "مجاني"
      : isFreeDelivery
      ? lang === "EN"
        ? `Free delivery on orders above $${shipping.free_threshold}`
        : `توصيل مجاني للطلبات فوق ${shipping.free_threshold}$`
      : `$${deliveryFee}`}
  </span>
</div>

        <div className="flex justify-between font-bold text-lg mt-2">
          <span>Total</span>
          <span>${((coupon?subtotal-coupon.discount:subtotal)+deliveryFee).toFixed(2)}</span>
        </div>
      </section>

      <button onClick={submitOrder} disabled={loading} className="w-full py-4 bg-[#2E5A2A] rounded-md font-semibold">
        {loading ? "..." : t.placeOrder}
      </button>
    </div>
  );
}

export default CheckoutPage;
